import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Label } from '../Form';
import { ApolloError, useMutation } from '@apollo/client';
import TesterPicker, { Tester } from '../Tester/TesterPicker';
import useNotify from '../../hooks/useNotify';
import { useHistory } from 'react-router-dom';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import FormRow from '../Form/FormRow';
import { IdArrayInput } from '../../hooks/useTablePages';
import { gql as company_gql } from '@/modes/company/gql';
import { gql as waterSupplier_gql } from '@/modes/waterSupplier/gql';
import useMode from '@/hooks/useMode';
import { UserMode } from '@/hooks/useApolloClient';

const CREATE_BACKFLOW_ASSEMBLY_JOBS_COMPANY = company_gql(`#graphql
  mutation CreateBackflowAssemblyJobs($input: BackflowAssemblyJobsInput!) {
    createBackflowAssemblyJobs(input: $input) {
      id
    }
  }
`);

const CREATE_BACKFLOW_ASSEMBLY_JOBS_WATER_SUPPLIER = waterSupplier_gql(`#graphql
  mutation CreateBackflowAssemblyJobs($input: BackflowAssemblyJobsInput!) {
    createBackflowAssemblyJobs(input: $input) {
      id
    }
  }
`);

export type OutboundAssignmentInput = {
  vendorProfileId: string | number;
  due?: number;
};

export type JobTypeKey =
  | 'BACKFLOW_TEST'
  | 'NEW_INSTALL'
  | 'DISCRETIONARY'
  | 'COMPLIANCE'
  | 'RESEARCH'
  | 'ADMINISTRATIVE'
  | 'RW_INSPECTION';

export type BackflowAssemblyJobsInput = {
  type: JobTypeKey;
  backflowAssemblyIds: IdArrayInput;
  outboundAssignment?: OutboundAssignmentInput;
};

type AddJobTracModalProps = {
  backflowAssemblyIds: IdArrayInput;
  onClose: (actionTaken: boolean) => void;
};

const AddAssemblyJobsModal: React.FC<AddJobTracModalProps> = ({
  backflowAssemblyIds,
  onClose,
}: AddJobTracModalProps) => {
  const [tester, setTester] = useState<Tester | null>(null);
  const [nestedTester, setNestedTester] = useState<Tester | null>(null);

  const { showError } = useNotify();
  const history = useHistory();
  const mode: UserMode = useMode();

  const canAssignNestedTester =
    mode === 'water-supplier' && tester?.profile?.type === 'COMPANY';

  const [createJobsCompany] = useMutation(
    CREATE_BACKFLOW_ASSEMBLY_JOBS_COMPANY
  );
  const [createJobsWaterSupplier] = useMutation(
    CREATE_BACKFLOW_ASSEMBLY_JOBS_WATER_SUPPLIER
  );

  const save = async () => {
    try {
      if (backflowAssemblyIds.length) {
        let result;

        if (mode === 'company') {
          result = await createJobsCompany({
            variables: {
              input: {
                type: 'BACKFLOW_TEST',
                backflowAssemblyIds: backflowAssemblyIds as string[],
                outboundAssignment: tester?.profileId
                  ? {
                      vendorProfileId: tester?.profileId,
                    }
                  : undefined,
              },
            },
          });
        }

        if (mode === 'water-supplier') {
          result = await createJobsWaterSupplier({
            variables: {
              input: {
                type: 'BACKFLOW_TEST',
                backflowAssemblyIds: backflowAssemblyIds as string[],
                outboundAssignment: tester?.profileId
                  ? {
                      vendorProfileId: tester?.profileId,
                      nestedAssignmentProfileId: nestedTester?.profileId,
                    }
                  : undefined,
              },
            },
          });
        }

        if (result?.data?.createBackflowAssemblyJobs?.length) {
          history.push(
            `/${mode === 'company' ? 'company' : 'water-supplier'}/jobs/${result?.data?.createBackflowAssemblyJobs[0].id}`
          );
        }
      }
      onClose(true);
    } catch (e) {
      showError('Error creating job', e as ApolloError);
    }
  };

  return (
    <Modal title="Create Backflow Tests" onClose={() => onClose(false)}>
      <ModalBody>
        <Form>
          <>
            <FormRow>
              <FormColumn>
                <Label>Assign to (Optional)</Label>
                <TesterPicker
                  testerProfileId={tester?.profileId}
                  isClearable
                  where={{
                    status: ['ACTIVE', 'PENDING'],
                  }}
                  onChange={setTester}
                />
              </FormColumn>
            </FormRow>
            {canAssignNestedTester && (
              <FormRow>
                <FormColumn>
                  <Label>Tester (Optional)</Label>
                  <TesterPicker
                    testerProfileId={nestedTester?.profileId}
                    companyProfileId={tester?.profileId}
                    onChange={setNestedTester}
                  />
                </FormColumn>
              </FormRow>
            )}
          </>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save}>
          Create{' '}
          {backflowAssemblyIds?.length > 1 ? backflowAssemblyIds?.length : ''}{' '}
          Job
          {backflowAssemblyIds?.length > 1 ? 's' : ''}
        </Button>
        <Button
          variant="outline"
          icon="slash-circle"
          onClick={() => onClose(false)}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddAssemblyJobsModal.propTypes = {
  onClose: PropTypes.any,
};

export default AddAssemblyJobsModal;
